// Standalone router-guard helpers.
//
// Lives in its own module so unit tests can import the helpers without
// pulling in src/router/index.ts (which imports every page component and
// transitively reaches jsdom-incompatible deps like @salutejs/jazz-sdk).
//
// Keep this file free of view/component imports — only types and the
// minimal store shape needed by each guard.

import type {
    LocationQueryRaw,
    RouteLocationNormalized,
    RouteLocationRaw,
} from 'vue-router';

/**
 * Issue #1 — anonymous click on the landing CTA «Стать наставником — заработать»
 * targets /become-mentor. The route is `requiresAuth: true`, so the global
 * guard would otherwise bounce the user to /login — but a brand-new visitor
 * has no account, so /login is a dead end. Send them to /register with the
 * mentor intent encoded in the query so RegisterPage can pre-select the role
 * and skip the intent-choice screen.
 *
 * Everything else continues to use the standard /login redirect.
 *
 * Attribution markers (utm_*, lid, source) are merged on top of this base
 * by the caller — they live in router/index.ts because they require store
 * + from-route context this helper deliberately does not touch.
 */
export function buildAuthRedirect(
    toFullPath: string,
): { path: string; query: LocationQueryRaw } {
    // Match exact /become-mentor and the trailing-slash typo form. We do not
    // match a prefix because no nested mentor-signup routes exist today; if
    // one is added, expand the predicate alongside it.
    const pathOnly = toFullPath.split('?')[0];
    const isMentorSignup = pathOnly === '/become-mentor' || pathOnly === '/become-mentor/';

    if (isMentorSignup) {
        return {
            path: '/register',
            query: { intent: 'mentor', redirect: toFullPath },
        };
    }

    return {
        path: '/login',
        query: { redirect: toFullPath },
    };
}

/**
 * Куда вернуть УЖЕ залогиненного пользователя, которого занесло на /login.
 *
 * Приоритет:
 *   1. явный `?redirect=` — его ставит httpClient при настоящей потере сессии
 *      (E10/E12/E13) и LoginPage после входа;
 *   2. страница, с которой пришли, если это живая страница приложения. Без
 *      этого шага разовый сбой refresh телепортировал человека с любой
 *      страницы на дефолтный /dashboard: httpClient уводил на голый /login,
 *      Vuex-user оставался жив, и гард отбрасывал обратно «в никуда» (E12);
 *   3. /dashboard — прямой заход на /login (from пустой: matched.length === 0).
 *
 * Результат всегда same-origin: протокол-относительные (`//evil.com/…`) и
 * абсолютные URL схлопываются в /dashboard — тот же предикат, что в
 * SocialCallback.vue и LoginPage.vue (см. safeRedirectPath.spec.ts).
 */
export function resolveAuthedLoginRedirect(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
): string {
    const fromIsLivePage =
        from.matched.length > 0 && from.path !== '/login' && from.path !== '/register';
    const fallback = fromIsLivePage ? from.fullPath : '/dashboard';
    const candidate = typeof to.query.redirect === 'string' ? to.query.redirect : fallback;

    return candidate.startsWith('/') && !candidate.startsWith('//') ? candidate : '/dashboard';
}

// Minimal shape the landing-redirect helper needs from the Vuex store.
// Typed against the concrete getters/state we touch rather than the full
// Vuex Store<...> so tests can pass a hand-rolled stub.
export interface AuthStoreLike {
    state: { userChecked: boolean };
    getters: { isAuthenticated: boolean; userRole?: string | null };
    dispatch: (action: string) => Promise<unknown>;
}

/**
 * Trap-prevention helper: returns a redirect target for landing pages
 * that don't make sense for the current user. Two meta flags supported:
 *
 *   - `redirectAuthedToDashboard: true` — redirect ANY authenticated user.
 *     Used on the generic /Home landing, root /, mentee-side LandingMenteePage:
 *     once you're logged in there's nothing useful for you on those splashes.
 *
 *   - `redirectMentorsToDashboard: true` — redirect ONLY users who are
 *     already mentors. Used on /NewLandingMentor — the mentor-recruiting
 *     landing should still be reachable for logged-in non-mentors who want
 *     to understand the role before applying (typical entry point: the
 *     "Узнать подробнее" CTA on /forbidden when a menti opens /Statistics).
 *
 * Cold start (userChecked === false): we await fetchUser once so we don't
 * leave an authed visitor on the marketing splash on first paint. If the
 * user is anonymous, fetchUser rejects and we fall through to the landing
 * as intended.
 *
 * Query params (utm_*, lid, etc.) are preserved through the redirect so
 * attribution markers carry into the dashboard session.
 */
export async function resolveLandingRedirect(
    to: RouteLocationNormalized,
    authStore: AuthStoreLike,
): Promise<{ path: string; query: RouteLocationNormalized['query'] } | null> {
    const wantsAuthedRedirect = Boolean(to.meta.redirectAuthedToDashboard);
    const wantsMentorRedirect = Boolean(to.meta.redirectMentorsToDashboard);
    if (!wantsAuthedRedirect && !wantsMentorRedirect) return null;

    if (!authStore.state.userChecked) {
        try {
            await authStore.dispatch('fetchUser');
        } catch {
            /* anonymous — fall through to the landing */
        }
    }

    if (!authStore.getters.isAuthenticated) return null;

    // Mentor-only redirect: non-mentor authed users see the landing.
    if (wantsMentorRedirect && !wantsAuthedRedirect) {
        if (authStore.getters.userRole === 'mentor') {
            return { path: '/dashboard', query: to.query };
        }
        return null;
    }

    // Default: any authed user → dashboard.
    return { path: '/dashboard', query: to.query };
}

// ── Доступ к разделу планирования (/CreateEntityPage) ──────────────────
//
// Доступ к планированию и СОЗДАНИЮ форматов определяет «наставнический трек», а
// НЕ лестница продаж (selling_readiness гейтит только публичную ВИДИМОСТЬ:
// бесплатные форматы при одобрении, платные — после payout). На треке:
// role=mentor (одобрен/ghost/claim) ИЛИ есть решающая заявка — pending («подал
// заявку»), rejected (можно дорабатывать), approved. Источник истины — флаг с
// бэка `can_access_planning`; чистый менти без заявки ведётся на подачу заявки
// (/become-mentor), а не в «глухой» /forbidden (баг 3.2).
//
// Фолбэк (устаревший payload без флага): роль наставника или readiness на треке.
const PLANNING_ALLOWED_READINESS = new Set([
    'application_pending',
    'approved_no_payout',
    'selling',
]);

// Минимальная форма стора для helper'а доступа к планированию: role +
// readiness + флаг can_access_planning на пользователе, dispatch('fetchUser')
// для реактуализации. Типизируем точечно, чтобы тесты могли передать стаб.
export interface PlanningStoreLike {
    state: {
        userChecked: boolean;
        user?: {
            role?: string | null;
            selling_readiness?: string;
            can_access_planning?: boolean;
        } | null;
    };
    dispatch: (action: string) => Promise<unknown>;
}

// Решение «на наставническом треке?»: приоритет — флаг с бэка; затем фолбэки
// (role=mentor, readiness на треке). rejected фолбэком НЕ покрывается — для
// него нужен свежий флаг, поэтому resolvePlanningAccess делает контрольный
// refetch, прежде чем окончательно отказать.
function canAccessPlanning(store: PlanningStoreLike): boolean {
    const user = store.state.user;
    if (!user) return false;
    if (user.can_access_planning === true) return true;
    if (user.role === 'mentor') return true;
    return PLANNING_ALLOWED_READINESS.has(user.selling_readiness ?? 'not_mentor');
}

/**
 * Решение о доступе к разделу планирования (/CreateEntityPage).
 *
 * Возвращает значение, пригодное прямо для navigation guard'а:
 *   - `true`                       → пускаем (на наставническом треке);
 *   - `{ path: '/become-mentor' }` → не на треке (чистый менти без заявки) —
 *     ведём на подачу заявки, а не в «глухой» /forbidden.
 *
 * Реактуализация без reload: если КЭШИРОВАННЫЙ пользователь блокирует доступ,
 * один раз перечитываем (fetchUser) и решаем по свежим данным — так свежий
 * флаг (одобрили/подали заявку в другой вкладке) разблокирует без перезагрузки.
 * fetchUser-исключение (аноним/сеть) трактуем как «доступа нет».
 */
export async function resolvePlanningAccess(
    store: PlanningStoreLike,
): Promise<true | { path: string }> {
    if (canAccessPlanning(store)) {
        return true;
    }

    // Кэш блокирует — возможно, он стейл (одобрили/подали заявку в другой
    // вкладке). Контрольный refetch ровно один раз, затем решаем по свежим данным.
    try {
        await store.dispatch('fetchUser');
    } catch {
        /* аноним / сетевой сбой — оставляем блокировку */
    }

    if (canAccessPlanning(store)) {
        return true;
    }
    return { path: '/become-mentor' };
}

// ── Доступ к каталогу наставников (/AllMentors) ────────────────────────
//
// Публичный каталог всех наставников пока не несёт смысла для обычных
// пользователей → временно доступен ТОЛЬКО операторам-админам. Признак
// «админ» берём из того же источника, что и /admin-*-роуты: /admin-api/me/
// (суперюзер ИЛИ любая admin.*-группа) через Pinia-стор useAdminMeStore.
// Не-админов (и анонимов, у которых пробник вернёт 403/401) уводим на
// /dashboard (для анонима глобальный auth-guard оттуда доведёт до /login).
//
// Минимальная форма стора инжектится параметром — так helper unit-тестируем
// без Pinia (тот же приём, что у resolveMentorSlug с инъекцией fetcher'а).
export interface AdminMeStoreLike {
    me: unknown | null;
    accessDenied: boolean;
    loading: boolean;
    isAdmin: boolean;
    load: () => Promise<void>;
}

/**
 * Гард каталога наставников (/AllMentors). Возвращает значение, пригодное
 * прямо для navigation guard'а:
 *   - `true`                     → пускаем (пользователь — админ);
 *   - `{ path: '/dashboard' }`   → не админ, уводим на дашборд.
 *
 * Если контекст админа ещё не загружен (и не в процессе/не отказано) —
 * подгружаем один раз перед решением.
 */
export async function resolveMentorsCatalogAccess(
    store: AdminMeStoreLike,
): Promise<true | RouteLocationRaw> {
    if (!store.me && !store.accessDenied && !store.loading) {
        await store.load();
    }
    return store.isAdmin ? true : { path: '/dashboard' };
}

/**
 * Гард каталога групповых сессий (/AllEvents): то же admin-only правило, что
 * и у каталога наставников — на запуске оба каталога закрыты от обычных
 * пользователей (решение владельца 13.08.2026). Отдельное имя, чтобы при
 * будущем открытии каталога событий не расцеплять его с наставниками вслепую.
 */
export const resolveEventsCatalogAccess = resolveMentorsCatalogAccess;

// Shape returned by GET /public/mentors/by-slug/{slug}/. Only the fields the
// resolver branches on are typed; the full payload is consumed by MentorPage.
export interface MentorSlugPayload {
    redirect_to?: string;
    [key: string]: unknown;
}

// Injected fetcher so the resolver stays unit-testable without the http
// client. In production this is `fetchPublicMentorBySlugApi` from services/api.
export type MentorSlugResolver = (slug: string) => Promise<MentorSlugPayload>;

// Structural 404 check. We deliberately avoid `axios.isAxiosError` here: the
// httpClient wrapper (services/httpClient.ts / api.js) preserves both the
// `isAxiosError` flag and `response.status` on the rejected error, so a plain
// shape check works without importing axios into the router layer.
function isNotFoundError(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
        return false;
    }
    const response = (error as { response?: { status?: unknown } }).response;
    return response?.status === 404;
}

/**
 * Bug 4.2 — pre-mount resolver for the public mentor profile route `/:slug`.
 *
 * The `/:slug` matcher swallows ANY single-segment URL before the
 * `/:pathMatch(.*)*` not-found route. Resolving the slug in the route's
 * `beforeEnter` guard — BEFORE MentorPage mounts — means a typo'd or stale
 * link lands on the honest 404 (correct «Страница не найдена» title, no mentor
 * chrome flash) instead of mounting the profile only to swap it out after the
 * backend 404.
 *
 * Returns a value suitable for direct use in a Vue Router navigation guard:
 *   - `true`            → allow navigation (slug resolved, or no slug to check,
 *                         or a non-404 error the component should own).
 *   - RouteLocationRaw  → redirect (to `not-found` on 404, or to the canonical
 *                         slug when the backend signals `redirect_to`).
 *
 * Valid profiles are never blocked: on success and on transient (non-404)
 * failures the guard returns `true` and MentorPage renders as before.
 */
export async function resolveMentorSlug(
    to: RouteLocationNormalized,
    fetchMentorBySlug: MentorSlugResolver,
): Promise<true | RouteLocationRaw> {
    const rawSlug = to.params.slug;
    const slug = typeof rawSlug === 'string' ? rawSlug : undefined;
    if (!slug) {
        return true;
    }

    try {
        const payload = await fetchMentorBySlug(slug);
        if (payload && typeof payload.redirect_to === 'string' && payload.redirect_to) {
            return { name: 'MentorPublic', params: { slug: payload.redirect_to } };
        }
        return true;
    } catch (error) {
        if (isNotFoundError(error)) {
            return { name: 'not-found' };
        }
        // Transient/unknown failure: let MentorPage render and own the
        // fallback rather than hard-blocking what may be a real profile.
        return true;
    }
}

// Ответ GET /users/{public_id}/ — типизируем только поля, по которым ветвимся.
export interface MentorByIdPayload {
    slug?: string | null;
    role?: string | null;
    [key: string]: unknown;
}

export type MentorByIdResolver = (mentorId: string) => Promise<MentorByIdPayload>;

/**
 * E50 — `/mentor_info/<public_id>` и `/<slug>` показывали разные данные.
 *
 * Это две несвязанные схемы загрузки внутри MentorPage: slug тянет собранный
 * публичный payload (события только будущие, featured-контент, og-мета,
 * `viewer_is_owner`/`owner_preview`), id — `GET /users/<uuid>/` плюс четыре
 * legacy-эндпоинта. Расхождение чинится не синхронизацией схем, а тем, что
 * наставника со слагом больше не показывают по id: гард уводит на канонический
 * адрес, и остаётся ровно одна схема.
 *
 * Роут при этом остаётся живым — он штатный фолбэк для профилей без слага и
 * внешняя поверхность (OG-вьюхи, CTA уведомлений, claim-страница, «Мои
 * наставники», календарь).
 *
 * Гард никогда не блокирует: нет слага, нет параметра, любая ошибка сети или
 * 404 — возвращаем `true`, и страница рендерится по id, как раньше.
 */
export async function resolveMentorInfoRedirect(
    to: RouteLocationNormalized,
    fetchMentorById: MentorByIdResolver,
): Promise<true | RouteLocationRaw> {
    const rawId = to.params.mentorId;
    const mentorId = typeof rawId === 'string' ? rawId.trim() : '';
    if (!mentorId) {
        return true;
    }

    try {
        const payload = await fetchMentorById(mentorId);
        const slug = typeof payload?.slug === 'string' ? payload.slug.trim() : '';
        // `/<slug>` резолвится только для наставников (by-slug фильтрует по
        // role=MENTOR), поэтому редиректим лишь их — иначе увели бы на 404.
        const isMentor = payload?.role === 'mentor';
        if (slug && isMentor) {
            // Query и hash переносим: /mentor_info/<id>?utm=… должен доехать
            // до slug-адреса вместе с разметкой источника.
            return { name: 'MentorPublic', params: { slug }, query: to.query, hash: to.hash };
        }
        return true;
    } catch {
        return true;
    }
}

/**
 * E41 — восстановление позиции скролла кнопкой «Назад».
 *
 * `scrollBehavior` отдаёт `savedPosition` синхронно, но целевая страница в этот
 * момент чаще всего ещё в состоянии loading (данные грузятся в `onMounted`,
 * контент под `v-if="!loading"`). Высота документа ≈ высоте вьюпорта, браузер
 * обрезает восстановленный offset до нуля — и пользователь оказывается наверху.
 *
 * Хелпер ждёт, пока документ дорастёт до сохранённой позиции, и только тогда
 * резолвится. Таймаут-фолбэк обязателен: контент мог измениться и такой высоты
 * уже не набрать (или страница отдала ошибку) — тогда браузер сам зажмёт
 * позицию в максимум, что и есть лучший возможный результат.
 *
 * Параметры окна/документа передаются извне, чтобы хелпер оставался
 * тестируемым и не зависел от глобалов.
 */
export const SCROLL_RESTORE_TIMEOUT_MS = 2000;

export interface ScrollRestoreDeps {
    /** Текущая полная высота документа. */
    getDocumentHeight: () => number;
    /** Высота вьюпорта — нужна, чтобы понять, доскроллится ли документ. */
    getViewportHeight: () => number;
    /** Планировщик следующей проверки (в рантайме — requestAnimationFrame). */
    schedule: (callback: () => void) => void;
    /** Часы — вынесены ради детерминированных тестов. */
    now: () => number;
    timeoutMs?: number;
}

export function waitForScrollTarget(top: number, deps: ScrollRestoreDeps): Promise<void> {
    return new Promise((resolve) => {
        const deadline = deps.now() + (deps.timeoutMs ?? SCROLL_RESTORE_TIMEOUT_MS);
        const tick = (): void => {
            const reachable =
                deps.getDocumentHeight() >= top + deps.getViewportHeight();
            if (reachable || deps.now() >= deadline) {
                resolve();
                return;
            }
            deps.schedule(tick);
        };
        tick();
    });
}
